import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineInboxArrowDown,
  HiOutlineArrowPath,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineMagnifyingGlass,
  HiOutlineChatBubbleLeftRight
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626"
};

function formatDisplayDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function requestTypeLabel(type) {
  if (type === "target_update") return "Target Update";
  if (type === "task_addition") return "Add Task To Practice";
  if (type === "practice_with_tasks") return "Add Practice With Tasks";
  return "Request";
}

function AdminInbox({ user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [replyMap, setReplyMap] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadRows();
  }, []);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await api.get("/manager-requests");
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);

      const nextReplies = {};
      data.forEach((row) => {
        nextReplies[row.id] = row.admin_reply || "";
      });
      setReplyMap(nextReplies);
    } catch (err) {
      console.error("Admin inbox load failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      setActionLoadingId(id);

      await api.put(`/manager-requests/${id}`, {
        status,
        admin_reply: replyMap[id] || ""
      });

      await loadRows();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to update request");
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) =>
      [
        row.manager_name,
        row.request_type,
        row.practice,
        row.task,
        row.message,
        row.status,
        row.admin_reply,
        row.requested_target
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const getStatusUI = (status) => {
    const s = String(status || "pending").toLowerCase();

    if (s === "approved") {
      return {
        text: "Approved",
        color: theme.success,
        bg: "#DCFCE7",
        icon: <HiOutlineCheckCircle />
      };
    }

    if (s === "rejected") {
      return {
        text: "Rejected",
        color: theme.danger,
        bg: "#FEE2E2",
        icon: <HiOutlineXCircle />
      };
    }

    return {
      text: "Pending",
      color: theme.warning,
      bg: "#FEF3C7",
      icon: <HiOutlineClock />
    };
  };

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Admin Inbox</h2>
          <p style={subtitle}>
            Review manager requests and approve or reject them with a reply.
          </p>
        </div>

        <button onClick={loadRows} style={refreshBtn} type="button">
          <HiOutlineArrowPath style={{ fontSize: 16 }} />
          Refresh
        </button>
      </div>

      <div style={toolbar}>
        <div style={searchWrap}>
          <HiOutlineMagnifyingGlass style={searchIcon} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search inbox..."
            style={searchInput}
          />
        </div>
      </div>

      {loading ? (
        <div style={stateCard}>
          <HiOutlineInboxArrowDown style={{ fontSize: 24, color: theme.primary }} />
          <span>Loading inbox...</span>
        </div>
      ) : filteredRows.length === 0 ? (
        <div style={stateCard}>
          <HiOutlineInboxArrowDown style={{ fontSize: 24, color: theme.primary }} />
          <span>No manager requests found</span>
        </div>
      ) : (
        <div style={list}>
          {filteredRows.map((row) => {
            const isPending = String(row.status || "pending").toLowerCase() === "pending";
            const isBusy = actionLoadingId === row.id;
            const statusUI = getStatusUI(row.status);

            return (
              <div key={row.id} style={card}>
                <div style={cardTop}>
                  <div>
                    <div style={requestTitle}>
                      {requestTypeLabel(row.request_type)}
                    </div>
                    <div style={requestMeta}>
                      {row.manager_name || "Manager"} • {formatDisplayDate(row.created_at)}
                    </div>
                  </div>

                  <span
                    style={{
                      ...statusPill,
                      color: statusUI.color,
                      background: statusUI.bg
                    }}
                  >
                    {statusUI.text}
                  </span>
                </div>

                <div style={detailGrid}>
                  <div style={infoLine}>
                    <strong>Practice:</strong> {row.practice || "-"}
                  </div>

                  <div style={infoLine}>
                    <strong>
                      {row.request_type === "practice_with_tasks" ? "Tasks" : "Task"}:
                    </strong>{" "}
                    {row.task || "-"}
                  </div>

                  {row.request_type === "target_update" && (
                    <div style={infoLine}>
                      <strong>Requested Target:</strong> {row.requested_target || "-"}
                    </div>
                  )}
                </div>

                <div style={messageBlock}>
                  <strong>Message:</strong> {row.message || "-"}
                </div>

                <div style={replySection}>
                  <div style={replyLabel}>
                    <HiOutlineChatBubbleLeftRight style={{ fontSize: 16 }} />
                    Reply to Manager
                  </div>

                  <textarea
                    value={replyMap[row.id] || ""}
                    onChange={(e) =>
                      setReplyMap((prev) => ({
                        ...prev,
                        [row.id]: e.target.value
                      }))
                    }
                    placeholder="Type your reply..."
                    style={textarea}
                  />
                </div>

                <div style={buttonRow}>
                  <button
                    type="button"
                    onClick={() => handleAction(row.id, "approved")}
                    disabled={!isPending || isBusy}
                    style={{
                      ...approveBtn,
                      opacity: !isPending || isBusy ? 0.6 : 1,
                      cursor: !isPending || isBusy ? "not-allowed" : "pointer"
                    }}
                  >
                    {isBusy ? "Processing..." : "Approve"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAction(row.id, "rejected")}
                    disabled={!isPending || isBusy}
                    style={{
                      ...rejectBtn,
                      opacity: !isPending || isBusy ? 0.6 : 1,
                      cursor: !isPending || isBusy ? "not-allowed" : "pointer"
                    }}
                  >
                    {isBusy ? "Processing..." : "Reject"}
                  </button>
                </div>

                {!isPending && row.admin_reply ? (
                  <div style={existingReplyBox}>
                    <strong>Saved Reply:</strong> {row.admin_reply}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const page = {
  color: theme.text,
  fontFamily: "sans-serif"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "24px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 700
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px"
};

const refreshBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  border: "none",
  background: "linear-gradient(135deg, #0F766E, #14B8A6)",
  color: "#FFFFFF",
  padding: "12px 16px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 700
};

const toolbar = {
  marginBottom: "18px"
};

const searchWrap = {
  position: "relative",
  width: "320px",
  maxWidth: "100%"
};

const searchIcon = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "18px"
};

const searchInput = {
  width: "100%",
  height: "42px",
  borderRadius: "12px",
  border: "1px solid #CBD5E1",
  padding: "0 12px 0 38px",
  fontSize: "14px",
  boxSizing: "border-box"
};

const stateCard = {
  minHeight: "150px",
  border: "1px dashed #CBD5E1",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  color: theme.textMuted
};

const list = {
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const card = {
  background: "#FFFFFF",
  borderRadius: "18px",
  padding: "18px",
  border: "1px solid #E2E8F0",
  boxShadow: "0 10px 24px rgba(15,23,42,0.05)"
};

const cardTop = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
  marginBottom: "14px"
};

const requestTitle = {
  fontSize: "16px",
  fontWeight: 700
};

const requestMeta = {
  marginTop: "4px",
  fontSize: "12px",
  color: theme.textMuted
};

const statusPill = {
  padding: "6px 12px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  whiteSpace: "nowrap"
};

const detailGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px",
  marginBottom: "12px"
};

const infoLine = {
  fontSize: "13px",
  color: theme.text,
  whiteSpace: "pre-wrap"
};

const messageBlock = {
  marginTop: "8px",
  marginBottom: "14px",
  background: "#F8FAFC",
  padding: "12px",
  borderRadius: "12px",
  fontSize: "13px",
  whiteSpace: "pre-wrap"
};

const replySection = {
  marginBottom: "14px"
};

const replyLabel = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "13px",
  fontWeight: 700,
  marginBottom: "8px",
  color: theme.text
};

const textarea = {
  width: "100%",
  minHeight: "90px",
  borderRadius: "12px",
  border: "1px solid #CBD5E1",
  padding: "12px",
  fontSize: "14px",
  boxSizing: "border-box",
  resize: "vertical"
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  marginTop: "8px"
};

const approveBtn = {
  border: "none",
  background: "#16A34A",
  color: "#FFFFFF",
  padding: "10px 16px",
  borderRadius: "10px",
  fontWeight: 700
};

const rejectBtn = {
  border: "none",
  background: "#DC2626",
  color: "#FFFFFF",
  padding: "10px 16px",
  borderRadius: "10px",
  fontWeight: 700
};

const existingReplyBox = {
  marginTop: "14px",
  background: "#ECFEFF",
  border: "1px solid #CCFBF1",
  borderRadius: "12px",
  padding: "10px 12px",
  fontSize: "13px",
  color: theme.text,
  whiteSpace: "pre-wrap"
};

export default AdminInbox;